const API_BASE_URL = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/+$/, '');

const GLOBAL_API_ERROR_EVENT = 'app-api-error';

const buildUrl = (path) => {
    if (/^https?:\/\//i.test(path)) {
        return path;
    }

    if (!path.startsWith('/')) {
        return `${API_BASE_URL}/${path}`;
    }

    return `${API_BASE_URL}${path}`;
};

const getAuthToken = () => {
    return localStorage.getItem('token');
};

const parseResponseBody = async (response) => {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    try {
        const text = await response.text();
        return text || null;
    } catch {
        return null;
    }
};

const extractErrorMessage = (payload, fallbackMessage) => {
    if (typeof payload === 'string' && payload.trim()) {
        return payload;
    }

    if (payload && typeof payload === 'object') {
        if (Array.isArray(payload.errors) && payload.errors.length > 0) {
            const firstError = payload.errors[0];
            if (typeof firstError === 'string' && firstError.trim()) {
                return firstError;
            }

            if (typeof firstError?.message === 'string' && firstError.message.trim()) {
                return firstError.message;
            }
        }

        if (payload.errors && typeof payload.errors === 'object') {
            const firstFieldError = Object.values(payload.errors)
                .flatMap((value) => Array.isArray(value) ? value : [value])
                .find((value) => typeof value === 'string' && value.trim());

            if (firstFieldError) {
                return firstFieldError;
            }
        }

        if (typeof payload.message === 'string' && payload.message.trim()) {
            return payload.message;
        }
    }

    return fallbackMessage;
};

const emitGlobalApiError = (error) => {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new CustomEvent(GLOBAL_API_ERROR_EVENT, {
        detail: {
            message: error.message,
            statusCode: error.statusCode,
        },
    }));
};

export const apiErrorEventName = GLOBAL_API_ERROR_EVENT;

export const apiRequest = async (path, {
    method = 'GET',
    headers = {},
    body,
    auth = true,
    defaultErrorMessage = 'Request failed',
    notifyOnError = true,
} = {}) => {
    const token = getAuthToken();
    const hasBody = body !== undefined && body !== null;

    const response = await fetch(buildUrl(path), {
        method,
        headers: {
            Accept: 'application/json',
            ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
            ...(auth && token ? { Authorization: 'Bearer ' + token } : {}),
            ...headers,
        },
        body: hasBody ? JSON.stringify(body) : undefined,
    });

    const payload = await parseResponseBody(response);

    if (!response.ok) {
        const error = new Error(extractErrorMessage(payload, defaultErrorMessage));
        error.statusCode = response.status;
        error.status = response.status;
        error.response = payload;

        if (notifyOnError && response.status >= 400 && response.status < 600) {
            emitGlobalApiError(error);
        }

        throw error;
    }

    if (response.status === 204) {
        return null;
    }

    return payload;
};
